/**
 * @file        operations.js           A module which formally defines various string constants for
 *                                      proxyAuth.operation, and which understands various inheritance
 *                                      relationships  (for example, that job.create can be satisfied
 *                                      by job).
 *
 *                                      Operations are most-to-least specific in order of node length.
 *                                      'bank' grants access to 'bank.account', but 'ba' does not grant
 *                                      access to 'bank'.
 *
 *                                      Each export from this module is one top-level operation key. Every
 *                                      operation key has the methods `allows` and `allowedBy`.
 *
 * @author      Wes Garland, wes@kingsds.network
 * @date        June 2021
 *
 * @example ```javascript
 *
 * const job = require('dcp/proxy-auth').operations.job;
 *
 * if (job.deploy.allowedBy(usersAccessLevel))
 *   console.log('deploy allowed if other stuff matches, too');
 *
 * ```
 */
'use strict';

const { OperationKey, keyPatterns } = require('./operation-key');

var operationKeyNames = [
  '$login',                     /* Magic auth level: the user is fully logged in: *everything* is allowed by this operation key, must be alphabetically lowest! */
  'portal',                     /* required to use the web portal back end */
  'portal.preferences.get',
  'portal.preferences.change',
  'portal.identity.revoke',
  'portal.identity.revealAddress',
  'portal.wallet',              /* allowed to do anything with respect to the wallet page in the portal */
  'portal.wallet.add',
  'portal.wallet.change',
  'portal.wallet.delete',
  'portal.wallet.get',
  'portal.wallet.list',         /* list banks accounts in portal */
  'portal.userInfo.get',

  'portal.jnu',
  'portal.jnu.<table>',
  'portal.jnu.<table>.add',
  'portal.jnu.<table>.add.changeMiss',
  'portal.jnu.<table>.change',
  'portal.jnu.<table>.delete',
  'portal.jnu.<table>.query',

  'scheduler.jnu',
  'scheduler.jnu.<table>.add.changeMiss',
  'scheduler.jnu.<table>',
  'scheduler.jnu.<table>.add',
  'scheduler.jnu.<table>.add.changeMiss',
  'scheduler.jnu.<table>.change',
  'scheduler.jnu.<table>.delete',
  'scheduler.jnu.<table>.query',

  'pxAuth.new',                     /* can make new proxy keys at current or subset of current perms */
  'pxAuth.renew',
  'pxAuth.worker.register',         /* can register workers */
  'pxAuth.jnu',
  'pxAuth.jnu.<table>',
  'pxAuth.jnu.<table>.add',
  'pxAuth.jnu.<table>.add.changeMiss',
  'pxAuth.jnu.<table>.change',
  'pxAuth.jnu.<table>.delete',
  'pxAuth.jnu.<table>.query',

  'entity.worker',                  /* can act as a given worker */

  'job.exec',                       /* dispatch a job: special, auto-extends to job.* related:jobId */
  'job.close',
  'job.events',
  'job.results',
  'job.results.fetch',
  'job.results.delete',
  'job.cancel',
  'job.data',
  'job.data.add',
  'job.computeGroup',
  'job.computeGroup.add',

  'worker',
  'worker.admin',

  /** NYI BELOW */

  'bank.account.balance',       /* view the account balance */
  'bank.account.link.job',      /* link a bank account to a job, related = job opaqueId */
  'bank.account.link.job.new',  /* link a bank account to a job as it is submitted */
  'bank.account.transfer',      /* transfer DCC out of the account, including cash out */
  'bank.account.transfer.job',  /* transfer DCC out of the account, but only for a specific job; related=opaqueId */
  'bank.account.transfer.autoPay',
  'bank.account.transfer.autoPay.attach',
  'bank.account.transfer.autoPay.slicePayment',
  'bank.account.transfer.autoPay.library',
];

for (let i=0; i < operationKeyNames.length; i++)
{
  const keyName = operationKeyNames[i];
  const lBkt = keyName.indexOf('<');
  if (lBkt === -1)
    continue;
  const rBkt = keyName.indexOf('>');
  const patternType = keyName.slice(0, lBkt - 1); /* scheduler.jnu */
  const patternName = keyName.slice(lBkt + 1, rBkt)
  const remain = keyName.slice(rBkt + 1);

  for (const subst of keyPatterns[patternType][patternName]) /* eg keyPatterns.scheduler.jnu.table */
  {
    const newName = `${patternType}.${subst}${remain}`;
    if (operationKeyNames[i] === keyName)
      operationKeyNames[i] = newName;
    else
      operationKeyNames.push(newName);
  }
}

function addOperation(parentOperationKey, operationKeyName)
{
  var i = operationKeyName.indexOf('.');

  if (i === -1)
    parentOperationKey[operationKeyName] = new OperationKey(operationKeyName, parentOperationKey);
  else
  {
    let name = operationKeyName.slice(0, i);
    let tail = operationKeyName.slice(i + 1);

    if (!parentOperationKey.hasOwnProperty(name))
      parentOperationKey[name] = new OperationKey(name, parentOperationKey);
    addOperation(parentOperationKey[name], tail);
  }
}

/* Exports object is built here.  When operationKeyNames includes "a.b.c",
 * we add exports.a.b.c.  Every ownProperty of every export has a toString
 * method which resolves to its full dot path.
 */
for (let operationKeyName of operationKeyNames)
  addOperation(exports, operationKeyName);

/* All exports from this module must be operation keys, so exporting key names must not happen via
 * exports. Instead, we push the operation key names into that module after they have been calculated.
 * The calculation is triggered by this module's initialization, which happens in the proxy-auth
 * module's initialization (evaluation) OR by the evaluation of the dcp-client bundle. In dcp-client,
 * the dynamically-generated (template) keyPatterns are memoized at bundle build time from the rtlink
 * version of the code.
 */
require('../pxa').operationKeyNames = operationKeyNames;
